import { useLanguage } from '@/context/LanguageContext';
import { translations } from '@/data/translations';
import { ContactForm } from '@/components/ContactForm';
import { Separator } from '@/components/ui/separator';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

export default function ContactPage() {
  const { t } = useLanguage();

  return (
    <div className="py-12 md:py-24 px-6 md:px-12 lg:px-24" data-testid="contact-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <h1 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] tracking-tight mb-4">
            {t(translations.contact.title)}
          </h1>
          <p className="font-sans text-lg text-[#5D5D5D] leading-relaxed mb-4">
            {t(translations.contact.subtitle)}
          </p>
          <p className="font-sans text-sm text-[#8B5A2B] bg-[#8B5A2B]/10 px-4 py-3 rounded-sm">
            {t(translations.contact.generalInquiry)}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <ContactForm />
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Address */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="h-5 w-5 text-[#8B5A2B]" strokeWidth={1.5} />
                <h3 className="font-sans text-sm uppercase tracking-widest text-[#5D5D5D]">
                  {t(translations.contact.addressTitle)}
                </h3>
              </div>
              <address className="not-italic font-sans text-base text-[#2C2C2C] leading-relaxed">
                {t(translations.contact.addressLine1)}<br />
                {t(translations.contact.addressLine2)}<br />
                {t(translations.contact.addressLine3)}<br />
                {t(translations.contact.addressLine4)}
              </address>
            </div>

            <Separator className="bg-[#E5E5E5]" />

            {/* Phone & Email */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#8B5A2B]" strokeWidth={1.5} />
                <a 
                  href="tel:+393356917918" 
                  className="font-sans text-base text-[#2C2C2C] hover:text-[#8B5A2B] transition-colors"
                  data-testid="contact-phone"
                >
                  +39 335 691 7918
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#8B5A2B]" strokeWidth={1.5} />
                <a 
                  href="mailto:info@helmutsantifaller.com" 
                  className="font-sans text-base text-[#2C2C2C] hover:text-[#8B5A2B] transition-colors"
                  data-testid="contact-email"
                >
                  info@helmutsantifaller.com
                </a>
              </div>
            </div>

            <Separator className="bg-[#E5E5E5]" />

            {/* Opening Hours */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Clock className="h-5 w-5 text-[#8B5A2B]" strokeWidth={1.5} />
                <h3 className="font-sans text-sm uppercase tracking-widest text-[#5D5D5D]">
                  {t(translations.contact.hoursTitle)}
                </h3>
              </div>
              <div className="font-sans text-base text-[#2C2C2C] space-y-1">
                <p>{t(translations.contact.hoursWeekdays)}</p>
                <p className="text-[#5D5D5D]">{t(translations.contact.hoursSaturday)}</p>
                <p className="text-[#5D5D5D]">{t(translations.contact.hoursSunday)}</p>
              </div>
            </div>

            <Separator className="bg-[#E5E5E5]" />

            {/* Note */}
            <div className="border-l-2 border-[#8B5A2B]/40 pl-4">
              <p className="font-sans text-sm text-[#5D5D5D] leading-relaxed">
                <strong className="text-[#2C2C2C]">{t(translations.contact.visitNoteTitle)}</strong><br /> 
                {t(translations.contact.visitNoteText)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
